import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Associates from "./Associates";

const LoanEMICalculator = () => {
    const getStoredValue = (key, defaultValue) => {
        const storedValue = sessionStorage.getItem(key);
        return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    };
    const [loanAmount, setLoanAmount] = useState(() => getStoredValue("loanAmount", 127588));
    const [interestRate, setInterestRate] = useState(() => getStoredValue("interestRate", 19.5));
    const [loanTenure, setLoanTenure] = useState(() => getStoredValue("loanTenure", 360));
    const navigate = useNavigate();

    const r = interestRate / 12 / 100;
    const n = loanTenure;
    const EMI = (loanAmount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalPayment = EMI * n;
    const totalInterest = totalPayment - loanAmount;

    const principal = loanAmount;
    const total = totalPayment;
    const principalPercentage = (principal / total) * 100;
    const interest = total - principal;
    const interestPercentage = 100 - principalPercentage;

    const [selected, setSelected] = useState("principal");
    const circleRef = useRef(null);

    useEffect(() => sessionStorage.setItem("loanAmount", JSON.stringify(loanAmount)), [loanAmount]);
    useEffect(() => sessionStorage.setItem("interestRate", JSON.stringify(interestRate)), [interestRate]);
    useEffect(() => sessionStorage.setItem("loanTenure", JSON.stringify(loanTenure)), [loanTenure]);

    const handleClick = (event) => {
        if (!circleRef.current) return;

        const rect = circleRef.current.getBoundingClientRect();
        const x = event.clientX - rect.left - rect.width / 2;
        const y = event.clientY - rect.top - rect.height / 2;
        const angle = (Math.atan2(y, x) * 180) / Math.PI + 90;
        const normalizedAngle = angle < 0 ? 360 + angle : angle;

        if (normalizedAngle <= (principalPercentage / 100) * 360) {
            setSelected("principal");
        } else {
            setSelected("interest");
        }
    };

    const formatINR = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const handleInputChange = (setter, key) => (e) => {
        let value = e.target.value;
        if (value === "") {
            setter("");
        } else {
            value = value.replace(/^0+/, ""); // Remove leading zeros
            setter(Number(value));
        }
    };

    const handleBlur = (setter, defaultValue) => () => {
        setter((prev) => (prev === "" ? defaultValue : prev));
    };

    return (
        <>
            <div className="other-pages-main-section emi-page-fin">
                <h2 className="other-pages-title">Loan EMI Calculator</h2>
                <p>
                    <span onClick={() => navigate("/")} style={{ cursor: "pointer" }}>Home</span> /
                    <span onClick={() => navigate("/emi-calculator-loan")} style={{ cursor: "pointer" }}>EMI Calculator</span>
                </p>
            </div>
            <Associates />
            <div className="loan-calculator-container-smco">
                {/* Left Section - Input Controls */}
                <div className="loan-calculator-input-section-smco">
                    <label>Loan Amount:</label>
                    <input
                        type="number"
                        value={loanAmount}
                        onChange={handleInputChange(setLoanAmount, "loanAmount")}
                        onBlur={handleBlur(setLoanAmount, 1000)}
                    />
                    <Slider
                        min={10000}
                        max={1000000000}
                        value={loanAmount}
                        onChange={setLoanAmount}
                        trackStyle={{ backgroundColor: "#0055cc" }}
                        handleStyle={{ borderColor: "#0055cc" }}
                    />

                    <label>Interest Rate (%):</label>
                    <input
                        type="number"
                        value={interestRate}
                        onChange={handleInputChange(setInterestRate, "interestRate")}
                        onBlur={handleBlur(setInterestRate, 1)}
                    />
                    <Slider
                        min={1}
                        max={100}
                        step={0.1}
                        value={interestRate}
                        onChange={setInterestRate}
                        trackStyle={{ backgroundColor: "#0055cc" }}
                        handleStyle={{ borderColor: "#0055cc" }}
                    />

                    <label>Loan Tenure (Months):</label>
                    <input
                        type="number"
                        value={loanTenure}
                        onChange={handleInputChange(setLoanTenure, "loanTenure")}
                        onBlur={handleBlur(setLoanTenure, 12)}
                    />
                    <Slider
                        min={12}
                        max={480}
                        step={12}
                        value={loanTenure}
                        onChange={setLoanTenure}
                        trackStyle={{ backgroundColor: "#0055cc" }}
                        handleStyle={{ borderColor: "#0055cc" }}
                    />
                </div>

                {/* Middle Section - EMI Details */}
                <div className="loan-calculator-emi-details-smco">
                    <h2>Your EMI Details</h2>
                    <p><div>Loan EMI:</div> <div><strong>{formatINR(EMI)}</strong></div></p>
                    <p><div>Total Interest Payable:</div> <div><strong>{formatINR(totalInterest)}</strong></div></p>
                    <p><div>Total Payment:</div> <div><strong>{formatINR(totalPayment)}</strong></div></p>
                </div>

                {/* Right Section - Pie Chart */}
                <div className="loan-calculator-chart-section-smco">
                    <div className="loan-calculator-chart-container-smco">
                        <div
                            ref={circleRef}
                            className="loan-calculator-circle-smco"
                            onClick={handleClick}
                            style={{
                                background: `conic-gradient(
                ${selected === "principal" ? "#000000" : "rgba(0, 0, 0, 0.3)"} 0% ${principalPercentage}%, 
                ${selected === "interest" ? "#0055cc" : "rgba(31, 124, 255, 0.3)"} ${principalPercentage}% 100%
            )`,
                                transition: "background 0.3s ease",
                                cursor: "pointer",
                            }}
                        >
                            <div className="loan-calculator-circle-inner-smco">
                                <span className="loan-calculator-label-smco">
                                    {selected === "principal" ? "Principal" : "Interest"}
                                </span>
                                <span className="loan-calculator-value-smco">
                                    {selected === "principal"
                                        ? principal.toLocaleString()
                                        : interest.toLocaleString()}
                                </span>
                            </div>
                        </div>
                        <div className="loan-calculator-legend-smco">
                            <div
                                onClick={() => setSelected("principal")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: selected === "principal" ? "bold" : "normal",
                                }}
                            >
                                <span className="loan-calculator-box-smco loan-calculator-principal-smco"></span> Principal Amount
                            </div>
                            <div
                                onClick={() => setSelected("interest")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: selected === "interest" ? "bold" : "normal",
                                }}
                            >
                                <span className="loan-calculator-box-smco loan-calculator-interest-smco"></span> Interest Amount
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoanEMICalculator;
